/**
 * Dataset Preview Map Component
 *
 * Displays a preview map of the full, unfiltered GeoJSON dataset.
 * Unlike QuizPreviewMap, this shows ALL features in the dataset without quiz-specific filters.
 */

import { useEffect, useState } from "react";
import { BaseMap } from "@/components/shared/BaseMap";
import { Card, CardContent } from "@/components/ui/card";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { PathOptions } from "leaflet";
import { fetchGeoJSONFromUrl } from "@/utils/geojsonLoader";

interface DatasetPreviewMapProps {
  datasetPath: string;
  className?: string;
  height?: string | number;
}

// Style for all features
const FEATURE_STYLE: PathOptions = {
  fillColor: "#3b82f6", // Blue-500
  color: "#ffffff",
  weight: 1,
  opacity: 0.8,
  fillOpacity: 0.4,
};

export function DatasetPreviewMap({ datasetPath, className, height = "280px" }: DatasetPreviewMapProps) {
  const [collection, setCollection] = useState<FeatureCollection<Geometry, GeoJsonProperties> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load the raw GeoJSON collection (no filters, no exclusions)
  useEffect(() => {
    const loadCollection = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load the raw dataset without any processing
        const rawCollection = await fetchGeoJSONFromUrl(datasetPath);

        setCollection(rawCollection);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to load dataset:", err);
        setError(err instanceof Error ? err.message : "Failed to load map");
        setIsLoading(false);
      }
    };

    loadCollection();
  }, [datasetPath]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-0 overflow-hidden rounded-lg">
          <div className="flex items-center justify-center bg-muted/30" style={{ height }}>
            <p className="text-sm text-muted-foreground">Loading dataset preview...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-0 overflow-hidden rounded-lg">
          <div className="flex items-center justify-center bg-muted/30" style={{ height }}>
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <Card className={className}>
      <CardContent className="p-0 overflow-hidden rounded-lg">
        <BaseMap
          data={collection}
          mapHeight={height}
          getFeatureStyle={() => FEATURE_STYLE}
          mapStyle="voyager"
          hideLabels={false}
          initialZoom={1}
        />
      </CardContent>
    </Card>
  );
}
